/**
 * Product Hub — how a product gets to the customer.
 *
 * **`resolveShippingRule`** — which of many `ShippingRules` applies — is the whole of it. Nine
 * specificity levels, and the same trap as the warehouse ladder: numeric `priority` orders *within* a
 * level and never across levels (ADR-009). The spec (§45) demands a test for it by name.
 *
 * What a product then *does* with that rule — the supplier, the delivery options, the store's
 * override — is `productHub.fulfillment.ts`. The split follows the spec's own seam: §38-40 and §45 are
 * about rules, §41-42 and §46-47 are about the product.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/shipping`.
 */
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

/**
 * A carrier's status — three values, not the module-wide `PH_ENTITY_STATUSES` pair.
 *
 * `paused` is the state the shared pair cannot express: a company we still work with and are not
 * sending anything to this month is neither active nor cancelled, and collapsing it into "inactive"
 * loses the only distinction anybody acts on — a paused carrier comes back, a cancelled one does not.
 * Archiving a carrier lands it on `cancelled` for that reason.
 */
export const SHIPPING_PROVIDER_STATUSES = ['active', 'paused', 'cancelled'] as const;
export type ShippingProviderStatus = (typeof SHIPPING_PROVIDER_STATUSES)[number];

export const SHIPPING_PROVIDER_STATUS_LABELS: Record<ShippingProviderStatus, string> = {
  active: 'פעיל',
  paused: 'מושהה',
  cancelled: 'מבוטל'
};

export const SHIPPING_PROVIDER_STATUS_OPTIONS: PhOption<ShippingProviderStatus>[] = phOptions(
  SHIPPING_PROVIDER_STATUSES,
  SHIPPING_PROVIDER_STATUS_LABELS
);

/**
 * How the goods travel (doc 2 §42) — the **legacy** codes, kept because rows hold them.
 *
 * This list used to *be* the catalogue of delivery methods: one row per code, the code chosen from a
 * closed dropdown, and the row's name derived from it. That capped the whole system at four methods
 * and made "משלוח בשליח עד הבית" impossible to add without a deploy. A method is now a row with a
 * **name somebody typed** (`SHIPPING_METHOD_KINDS` below carries the only part that has behaviour),
 * so this array is no longer a catalogue — it is the set of values the `type` column may hold.
 *
 * `pickup` is the one code anything branches on: it is what `canPickup` resolves to, it keeps a
 * carrier off the pickup method, and the picking flow reads it to decide whether anybody delivers.
 * `truck` and `special` survive only because existing rows say so — nothing new writes them.
 *
 * `supplier_direct` was removed with the suppliers tab: "the supplier ships it themselves" is already
 * a boolean on `ProductFulfillmentSettings` (ADR-005). Legacy rows were migrated to `standard`.
 */
export const SHIPPING_METHOD_TYPES = ['standard', 'truck', 'special', 'pickup'] as const;
export type ShippingMethodType = (typeof SHIPPING_METHOD_TYPES)[number];

export const SHIPPING_METHOD_TYPE_LABELS: Record<ShippingMethodType, string> = {
  standard: 'משלוח רגיל',
  truck: 'הובלה במשאית',
  special: 'משלוח מיוחד',
  pickup: 'איסוף עצמי'
};

export const SHIPPING_METHOD_TYPE_OPTIONS: PhOption<ShippingMethodType>[] = phOptions(
  SHIPPING_METHOD_TYPES,
  SHIPPING_METHOD_TYPE_LABELS
);

/**
 * What a method *does* — the only closed list left, and closed because code branches on it.
 *
 * Two values, not four: somebody either delivers the goods or the customer comes for them, and that
 * is the whole of what the system decides differently. Everything else about a method — that it goes
 * by truck, that it is express, that it is a courier to the door — is now its **name**, which is why
 * the type list stopped being the place new methods get added.
 */
export const SHIPPING_METHOD_KINDS = ['ship', 'pickup'] as const;
export type ShippingMethodKind = (typeof SHIPPING_METHOD_KINDS)[number];

export const SHIPPING_METHOD_KIND_LABELS: Record<ShippingMethodKind, string> = {
  ship: 'משלוח',
  pickup: 'איסוף עצמי'
};

export const SHIPPING_METHOD_KIND_OPTIONS: PhOption<ShippingMethodKind>[] = phOptions(
  SHIPPING_METHOD_KINDS,
  SHIPPING_METHOD_KIND_LABELS
);

/**
 * The kind a stored `type` means. Everything that is not `pickup` is somebody delivering.
 *
 * Deliberately total rather than a lookup: `truck` and `special` are legacy codes, and a row holding
 * one has to keep reading as "משלוח" rather than falling off a map.
 */
export const methodKindOf = (type: string | null | undefined): ShippingMethodKind =>
  type === 'pickup' ? 'pickup' : 'ship';

/**
 * The `type` a new method of this kind stores.
 *
 * New rows collapse onto the two codes anything actually branches on. Writing `standard` for every
 * shipped method is what lets the four-code column stay as it is: no migration, no widening, and
 * every `type !== 'pickup'` check in the resolver, the picking flow and the carrier form keeps
 * meaning what it meant.
 */
export const methodTypeForKind = (kind: ShippingMethodKind): ShippingMethodType =>
  kind === 'pickup' ? 'pickup' : 'standard';

/**
 * A method's Hebrew label from its legacy type code.
 *
 * Kept for rows whose `name` is somehow empty and for the audit trail; **not** the way a screen
 * names a method — that is `shippingMethodLabel`, which prefers the name somebody typed.
 */
export const shippingMethodTypeLabel = (type: string): string =>
  SHIPPING_METHOD_TYPE_LABELS[type as ShippingMethodType] ?? type;

/**
 * How every screen names a delivery method: **its own name**, with the type label as a floor.
 *
 * One helper rather than each screen deciding, because six of them show a method and two screens
 * naming the same row differently is a support call. The fallback matters for exactly one case — a
 * pre-existing row created when the name was derived and could in principle be blank.
 */
export const shippingMethodLabel = (method: { name?: string | null; type?: string | null }): string =>
  method.name?.trim() || shippingMethodTypeLabel(method.type ?? '');

/**
 * What a rule is *about*. The store is context on top of this, not one of these (§38).
 *
 * Codes only: the Hebrew labels and the options list went with the rules screen, and the resolver
 * branches on the codes. `ShippingRuleRow.targetType` is typed from this array.
 */
export const SHIPPING_TARGET_TYPES = ['variant', 'product', 'category', 'all'] as const;
export type ShippingTargetType = (typeof SHIPPING_TARGET_TYPES)[number];

/* ------------------------------------------------------------------ */
/* The seven-rung ladder                                               */
/* ------------------------------------------------------------------ */

/**
 * Which rung answered — the `source` half of §53's "effective value plus its source".
 *
 * Seven of them rather than four, because the store context doubles every level: "the rule for
 * furniture in the Israeli store" and "the rule for furniture everywhere" are different answers to
 * the same question, and a screen that cannot tell them apart cannot explain either.
 *
 * §45 describes **nine** rungs. The two family rungs went with `ProductFamilies`, which the module
 * no longer has. Nothing became inexpressible: the category rungs walk the **ancestor chain**, so a
 * rule written on a parent category catches every product beneath it — which is what a family rule
 * was doing.
 */
export const SHIPPING_RULE_LEVELS = [
  'store_variant',
  'store_product',
  'store_category',
  'global_variant',
  'global_product',
  'global_category',
  'global_default',
  'none'
] as const;
export type ShippingRuleLevel = (typeof SHIPPING_RULE_LEVELS)[number];

export const SHIPPING_RULE_LEVEL_LABELS: Record<ShippingRuleLevel, string> = {
  store_variant: 'כלל לחנות + וריאציה',
  store_product: 'כלל לחנות + מוצר',
  store_category: 'כלל לחנות + קטגוריה',
  global_variant: 'כלל גלובלי לוריאציה',
  global_product: 'כלל גלובלי למוצר',
  global_category: 'כלל גלובלי לקטגוריה',
  global_default: 'כלל ברירת המחדל',
  none: 'לא נמצא כלל שילוח'
};

/** One row of `ShippingRules`, as the resolver needs it. */
export interface ShippingRuleRow {
  id: number;
  name: string;
  /** `null` makes the rule global — the context half of the ladder (§38). */
  storeId: number | null;
  targetType: ShippingTargetType;
  variantId: number | null;
  productId: number | null;
  categoryId: number | null;
  shippingMethodId: number;
  /** Optional: a rule may name the method and leave the carrier to the method's own preference. */
  shippingProviderId: number | null;
  /** Orders rules **inside** one rung. Never across rungs. */
  priority: number;
  status: string;
}

/** What the resolver is asked about. `categoryIds` is the ancestor chain, deepest first. */
export interface ShippingRuleQuery {
  variantId: number;
  productId: number;
  /** The product's category and every ancestor above it, deepest first — a rule on a parent counts. */
  categoryIds: number[];
  storeId: number | null;
}

export interface ResolvedShippingRule {
  rule: ShippingRuleRow | null;
  level: ShippingRuleLevel;
  levelLabel: string;
  /** Every rule that matched the winning rung, in the order it was ordered — for a UI that explains. */
  candidateIds: number[];
}

/**
 * Which shipping rule applies — §45, and the trap in it.
 *
 * The ladder is walked top to bottom and the **first rung with any match at all** wins. Only inside
 * that rung does `priority ASC, id ASC` decide. The tempting shortcut — collect every matching rule
 * and sort the lot by `priority` — is shorter, looks equivalent, and is wrong: a global default with
 * `priority = 1` would beat a rule somebody wrote for one variant in one store with `priority = 100`.
 * Nothing would report a problem; furniture would simply ship by courier.
 *
 * The category rungs walk the ancestor chain deepest first, so a rule on the immediate category wins
 * over one on its parent. That is the same specificity idea one level down, and it is the reason the
 * caller has to pass the chain rather than a single id.
 */
export const resolveShippingRule = (rules: ShippingRuleRow[], query: ShippingRuleQuery): ResolvedShippingRule => {
  const active = rules.filter(rule => rule.status === 'active');

  const answer = (level: ShippingRuleLevel, matches: ShippingRuleRow[]): ResolvedShippingRule => {
    const ordered = sortWithinLevel(matches);
    return {
      rule: ordered[0],
      level,
      levelLabel: SHIPPING_RULE_LEVEL_LABELS[level],
      candidateIds: ordered.map(rule => rule.id)
    };
  };

  const inStore = (rule: ShippingRuleRow) => query.storeId !== null && rule.storeId === query.storeId;
  const global = (rule: ShippingRuleRow) => rule.storeId === null;

  const byVariant = (rule: ShippingRuleRow) => rule.targetType === 'variant' && rule.variantId === query.variantId;
  const byProduct = (rule: ShippingRuleRow) => rule.targetType === 'product' && rule.productId === query.productId;

  // Each context is tried against the whole category chain before moving down a rung, so
  // "store + parent category" still outranks "global + exact category" (§45's order).
  const categoryMatches = (context: (rule: ShippingRuleRow) => boolean): ShippingRuleRow[] => {
    for (const categoryId of query.categoryIds) {
      const matches = active.filter(
        rule => context(rule) && rule.targetType === 'category' && rule.categoryId === categoryId
      );
      if (matches.length) return matches;
    }
    return [];
  };

  const rungs: [ShippingRuleLevel, ShippingRuleRow[]][] = [
    ['store_variant', active.filter(rule => inStore(rule) && byVariant(rule))],
    ['store_product', active.filter(rule => inStore(rule) && byProduct(rule))],
    ['store_category', categoryMatches(inStore)],
    ['global_variant', active.filter(rule => global(rule) && byVariant(rule))],
    ['global_product', active.filter(rule => global(rule) && byProduct(rule))],
    ['global_category', categoryMatches(global)],
    // The catch-all rung. A store-scoped `all` rule is deliberately *not* a rung above it: the
    // store's own default belongs to the same rung as the global one, ordered by `priority` — and
    // the store-scoped row first, since it is the more specific of the two.
    ['global_default', active.filter(rule => rule.targetType === 'all' && (global(rule) || inStore(rule)))]
  ];

  for (const [level, matches] of rungs) {
    if (matches.length) return answer(level, matches);
  }

  return { rule: null, level: 'none', levelLabel: SHIPPING_RULE_LEVEL_LABELS.none, candidateIds: [] };
};

/**
 * `priority ASC`, then a store-scoped row before a global one, then `id ASC`.
 *
 * The middle key only ever matters on the `global_default` rung, where the two contexts share a
 * level. The `id` tiebreaker is beyond the spec and is the reason two rules with the same priority
 * do not swap answers between identical requests.
 */
const sortWithinLevel = (rules: ShippingRuleRow[]): ShippingRuleRow[] =>
  [...rules].sort(
    (a, b) => a.priority - b.priority || Number(b.storeId !== null) - Number(a.storeId !== null) || a.id - b.id
  );
